import { useEffect, useRef, useState } from 'react';
import { nanoid } from 'nanoid';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { FileInput, Radio } from '@mantine/core';

import { useDispatch } from 'react-redux';
import { tokenSelector, usernameSelector } from '../../store/slices/authSlice';
import {
  disclaimerAcceptedSelector,
  isLoggedinUserAdminSelector,
  privacyPolicyAcceptedSelector,
  setLoadingMessage,
  setShowGlobalLoading,
} from '../../store/slices/appSlice';

import useAuthenticate from '../../customHooks/authenticate';

import { AnalysisType, createLogRecord, ICreateLogRecord, queryFeatures } from '../../services/esri';
import { NotificationType, showNotification } from '../../utils/showNotifications';
import { mapController } from '../../controllers/MapController';
import { mapBController } from '../../controllers/MapBController';

import { executeAPIDetermination, executeAPIStatus } from '../../services/aws';
import { retryRequest } from '../../utils/retryRequest';
import { createDelay } from '../../utils';

import { requiredIcpMsHeaders, requiredSiraHeaders, requiredXrfHeaders, requiredSiraXRFHeaders } from '../../config/fileHeaders.config';
import { DISCLAIMER_URL } from '../../config/assets.url.config';
import { LAYERS_CONFIG } from '../../config/layer.config';
import { Method } from '../../config/s3publicfiles.config';

import GlobalLoader from '../../components/GlobalLoader/GlobalLoader';
import ShowResult from '../../components/ShowResult/ShowResult';
import WFIDButton from '../../components/WFIDButton/WFIDButton';
import DownloadSampleData from '../../components/DownloadSampleData/DownloadSampleData';
import DisclaimersComponent from '../../components/DisclaimersComponent/DisclaimersComponent';

import { mergeHeadersRowsData, replaceEmptyToNull } from '../Verification/helpers';

import { getErrorMessageBasedOnCode, StatusCode } from '../helpers';

import './Determination.css';

const { REFERENCE_DATA } = LAYERS_CONFIG;

const Determination = () => {
  const [selectedMethod, setSelectedMethod] = useState(Method.SIRA);
  const [showPostResultInfo, setShowPostResultInfo] = useState(false);
  const [postResultInfoMessage, setPostResultInfoMessage] = useState('');
  const [genusData, setGenusData] = useState(new Set());

  const [fileDataHashMap, setFileDataHashMap] = useState<any>(null);

  const [file, setFile] = useState<File | null>(null);
  const applicationDiv1 = useRef(null);
  const username = useSelector(usernameSelector);
  const token = useSelector(tokenSelector);
  const disclaimerAccepted = useSelector(disclaimerAcceptedSelector);
  const privacyPolicyAccepted = useSelector(privacyPolicyAcceptedSelector);
  const isLoggedinUserAdmin = useSelector(isLoggedinUserAdminSelector);
  const mapViewRef = useRef(null);
  const dispatch = useDispatch();

  useAuthenticate();

  const extractGenusData = async () => {
    try {
      const query = {
        where: '1=1',
        outFields: ['Genus'],
        returnGeometry: false,
        returnDistinctValues: true,
      };

      const response = await queryFeatures(query, REFERENCE_DATA.url);

      if (!response?.success) {
        // show Error message
        showNotification({
          type: NotificationType.Error,
          message: 'Error extracting genus data. Please try again.',
          title: 'Error',
        });
        return;
      }
      const extractedGenusData = new Set(response.data?.features.map((feature) => feature.attributes.Genus));
      setGenusData(extractedGenusData);
    } catch (error) {
      console.error('Error extracting genus data:', error);
    }
  };

  useEffect(() => {
    mapController.initializeMap(mapViewRef, applicationDiv1);

    return () => {
      mapController.destroyMap();
    };
  }, []);

  useEffect(() => {
    extractGenusData();
  }, []);

  const handleUploadButtonClick = async () => {
    dispatch(setShowGlobalLoading(true));
    setShowPostResultInfo(false);
    setPostResultInfoMessage('');
    if (file) {
      const reader = new FileReader();

      reader.onload = async (event: ProgressEvent<FileReader>) => {
        if (!event.target) return;
        const arrayBuffer = event.target.result as ArrayBuffer;
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const analysisID = nanoid();
        try {
          if (!username || !token) return;
          const params = { token, username, jsonData, analysisID };
          const startResponse = await executeAPIDetermination(params);
          dispatch(setLoadingMessage('Uploading file...'));

          if (!startResponse.ok) {
            throw new Error('Failed to upload file');
          }

          const startData = await startResponse.json();

          const executionArn = startData?.executionArn;

          // Poll the status endpoint to check execution status
          const pollStatus = async (executionArn: string) => {
            const params = { token, executionArn };
            const statusResponse = await executeAPIStatus(params);
            dispatch(setLoadingMessage('Analyzing data...'));

            if (!statusResponse.ok) {
              throw new Error('Failed to check execution status.');
            }

            const statusData = await statusResponse.json();
            const status = statusData?.status;
            if (status === 'RUNNING') {
              // If still running, wait and poll again
              setTimeout(() => pollStatus(executionArn), 5000);
            } else if (status === 'SUCCEEDED') {
              // Handle successful completion

              dispatch(setLoadingMessage('Processing data...'));
              const output = JSON.parse(statusData.output.body);
              const statusCode = statusData?.output?.statusCode;
              const notifictionMessage = getErrorMessageBasedOnCode(statusCode);

              if (statusCode !== StatusCode.OK) {
                showNotification({
                  type: NotificationType.Error,
                  message: notifictionMessage,
                  title: 'Error',
                  autoClose: false,
                });
                dispatch(setShowGlobalLoading(false));
                return;
              }

              const serviceUrl = output.publishResponse.services[0].serviceurl;
              const url = `${serviceUrl}/0`;
              const urlWitihToken = `${serviceUrl}/0?f=json&token=${token}`;

              const result = await retryRequest(urlWitihToken);

              if (!result?.success) {
                // show Error message
                showNotification({
                  type: NotificationType.Error,
                  message: 'Error loading feature layers.',
                  title: 'Error',
                });
                dispatch(setShowGlobalLoading(false));
                return;
              }

              const params = {
                where: 'in95=1',
                returnGeometry: true,
                outFields: ['*'],
              };
              dispatch(setLoadingMessage('Loading data...'));
              await createDelay(2000);
              const layerInfo = await queryFeatures(params, url);

              if (!layerInfo.success) {
                // show Error message
                showNotification({
                  type: NotificationType.Error,
                  message: 'Error querying features. Please try again.',
                  title: 'Error',
                  autoClose: false,
                });

                dispatch(setShowGlobalLoading(false));
                return;
              }
              if (layerInfo.data?.features.length === 0) {
                showNotification({
                  type: NotificationType.Error,
                  message:
                    'The sample provided does not correspond with any available reference data for the input genus',
                  title: 'Error',
                  autoClose: false,
                });

                dispatch(setShowGlobalLoading(false));

                return;
              }

              mapController.goTo(layerInfo.data as any);

              mapController.addLayer({
                url,
                expression: 'in95 = 1',
                title: '95%',
                id: '95',
              });
              mapController.addLayer({
                url,
                expression: 'in90 = 1',
                title: '90%',
                id: '90',
              });
              mapController.addLayer({
                url,
                expression: 'in85 = 1',
                title: '85%',
                id: '85',
              });

              dispatch(setLoadingMessage('Calculating data...'));
              const getCalculatedValues = mapBController.calculateTotalArea(layerInfo.data);
              if (!getCalculatedValues) {
                // show Error message
                showNotification({
                  type: NotificationType.Error,
                  message: 'Error calculating total area. Please try again.',
                  title: 'Error',
                  autoClose: false,
                });
                dispatch(setShowGlobalLoading(false));
                return;
              }

              // create log of analysis result to log table
              const logRecordParams: ICreateLogRecord = {
                hashMap: fileDataHashMap,
                featureUrl: url,
                analysisID,
                username,
                type: AnalysisType.Determination,
                ClaimedLocation: ''
              };
              // TOOD: we don't have a pdf report yet so we don't think to store the analysisID for the moment

              await createLogRecord(logRecordParams);

              setShowPostResultInfo(true);
              const { averageAreaKm2GeodesicRounded, totalPoints } = getCalculatedValues;
              setPostResultInfoMessage(
                `the total number of possible harvest locations where values are inferred is ${totalPoints}, with each location representing an average area of ${averageAreaKm2GeodesicRounded}km².`
              );

              dispatch(setLoadingMessage(''));
              dispatch(setShowGlobalLoading(false));
            } else {
              // Handle failure cases
              dispatch(setLoadingMessage(''));
              dispatch(setShowGlobalLoading(false));
              throw new Error(`Step Function execution failed with status: ${status}.`);
            }
          };
          // Start polling
          pollStatus(executionArn);
        } catch (error) {
          dispatch(setLoadingMessage(''));
          dispatch(setShowGlobalLoading(false));
          console.error('Error:', error);
        }
      };

      reader.readAsArrayBuffer(file);
    }
  };

  const handleSampleFileUpload = async (
    event: any
    //event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event;
    if (!file) return;
    setFile(file);
    mapController.removeAllLayers();
    mapBController.removeAllLayers();
    if (file) {
      // setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target === null) {
          console.error('FileReader target is null.');
          return;
        }
        const content = e.target.result;

        // Read the file as binary string
        const data = new Uint8Array(content as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });

        // Assuming the first sheet is the one you want to read
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];

        // Convert the worksheet to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        const headers = jsonData[0] as string[];
        const row = jsonData[1] as (string | number | null)[];

        const updatedRowData = replaceEmptyToNull(row, headers);
        const hashMap = mergeHeadersRowsData(updatedRowData, headers);
        setFileDataHashMap(hashMap);

        let requiredHeaders: string[] = [];
        if (selectedMethod === Method.SIRA) {
          requiredHeaders = requiredSiraHeaders;
        } else if (selectedMethod === Method.XRF) {
          requiredHeaders = requiredXrfHeaders;
        } else if (selectedMethod === Method.ICPMS) {
          requiredHeaders = requiredIcpMsHeaders;
        } else if (selectedMethod === Method.SIRAXRF) {
          requiredHeaders = requiredSiraXRFHeaders;
        }
        const missingHeaders = requiredHeaders.filter((header) => !headers.includes(header));
        const extraHeaders = headers.filter((header) => !requiredHeaders.includes(header));

        if (missingHeaders.length > 0 || extraHeaders.length > 0) {
          //let errorMessage = "";
          if (missingHeaders.length > 0) {
            /* errorMessage +=
                                                                                      "Missing headers: " + missingHeaders.join(", ") + ".\n"; */
          }
          if (extraHeaders.length > 0) {
            //errorMessage += "Extra headers: " + extraHeaders.join(", ") + ".\n";
          }
          //alert("Invalid Excel file.\n" + errorMessage);
          showNotification({
            type: NotificationType.Error,
            message: `The uploaded file does not match the selected analysis method. Please check you are using the latest template for ["${selectedMethod}"] and try again.`,
            title: 'Error Mismatch',
            autoClose: false,
          });
          return;
        }

        // Handle valid CSV file here

        // Extract the genus value (assuming there's a "Genus" column)
        const genusIndex = headers.indexOf('Genus');
        if (genusIndex === -1) {
          showNotification({
            type: NotificationType.Error,
            message: "The Genus column is missing from the uploaded file. Please check you are using the latest template and try again.",
            title: 'Error',
            autoClose: false,
          });
          return;
        }

        const genusValue = row[genusIndex];

        if (!genusData.has(genusValue)) {
          showNotification({
            type: NotificationType.Error,
            message: `Genus "${genusValue}" is not valid.`,
            title: 'Error',
            autoClose: false,
          });
          return;
        }
        showNotification({
          type: NotificationType.Success,
          message: 'Uploaded file is valid and has been processed.',
          title: 'Success',
          autoClose: true,
        });

        showNotification({
          type: NotificationType.Success,
          message: `Genus detected: ${genusValue}.`,
          title: 'Success',
          autoClose: true,
        });

        mapController.addSentineLayer();
        const genusName = genusValue as string;
        mapController.addGenusLayer(genusName);

        // filter the reference data to only show the samples with a Genus attribute in the values of the csvGenusSet set

        //const defExpression = ` AND Genus IN ('${genusName}') AND Technique LIKE '%${selectedMethod}%'`;
        // hide the two layers from the topmap until a valid file is uploaded
        //mapController.addReferenceDatLayer(defExpression);
        mapController.addGenusRangeFeatureLayer(genusName);
      };
      reader.readAsArrayBuffer(file);
    }
  };
  const handleSelectionMethod = (event: any, method: any) => {
    //const value = event.target.value as Method;
    console.log(event);
    setFile(null);
    setSelectedMethod(method);
    //setSelectedMethod(value);
  };
  const handleFileChange = (value: any) => {
    setFile(value);
    setShowPostResultInfo(false);
    setPostResultInfoMessage('');

    handleSampleFileUpload(value);
  };

  const handleReset = () => {
    mapController.removeLayerById('95%');
    mapController.removeLayerById('90%');
    mapController.removeLayerById('85%');
    setFile(null);
    setPostResultInfoMessage('');
    setShowPostResultInfo(false);
  };

  if (!isLoggedinUserAdmin) return null;
  return (
    <div className='main-wrapper'>
      <GlobalLoader />

      <div className='left-side'>
        <p className='panel-description'>
          This page helps you identify the most likely geolocation range in which your supply chain sample was
          harvested. Results are displayed with confidence level bands to help you assess areas that align with your
          risk tolerance.
        </p>
        <p className='panel-description'>
          The platform holds reference data for Stable Isotope Ratio Analysis (SIRA) and two methods of Multi-Element
          Analysis (MEA): XRF and ICPMS.
        </p>
        <p className='panel-description'>
          Currently, the platform can only compare measurements from one analysis method at a time, but we aim to
          develop the functionality to allow users to compare with multiple chemical reference sets simultaneously,
          pending funding.
        </p>

        <div className='analysis-container'>
          <div className='analysis-container-inner'>
            <p>
              <span className='method-selection-step'>1.</span>
              Download chemical data template
            </p>
            <p>
              Download the template spreadsheets to ensure that your chemical data is formatted correctly. Please ensure
              that you have the latest format before uploading. All measurements should be taken by a laboratory using
              preparation and measurement protocols that are comparable with the reference data in the platform in order
              to ensure reliable results.
            </p>
            <DownloadSampleData />

            <div className='analysis-method-selection'>
              <p className=''>
                <span className='method-selection-step'>2.</span>
                Select chemical method
              </p>
              <p className=''>Choose which chemical method was used to generate your supply chain sample data.</p>
              <div className='analysis-method-container'>
                {[Method.SIRA, Method.XRF, Method.ICPMS, Method.SIRAXRF].map((method) => (
                  <div key={method} className='radio-container'>
                    <Radio
                      onChange={(e) => handleSelectionMethod(e, method)}
                      checked={selectedMethod === method}
                      label={method}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className='upload-file-container'>
              <p className=''>
                <span className='method-selection-step'>3.</span>
                Upload chemical data
              </p>
              <p className=''>
                Upload the formatted supply chain sample spreadsheet. You must use the spreadsheet template
                corresponding to the selected chemical method. The spreadsheet must contain ONE sample only. Additional
                rows will be ignored or may prevent the tool from running. We aim to develop bulk analysis
                functionality, pending funding.
              </p>

              <div className='file-upload-wrapper'>
                <FileInput
                  placeholder='Choose file'
                  className='file-upload'
                  value={file}
                  withAsterisk
                  color='red'
                  variant='deafult'
                  size='md'
                  w={120}
                  accept='.xlsx'
                  onChange={handleFileChange}
                />
              </div>
            </div>
            {/*Disclaimers component */}
            <DisclaimersComponent />

            <div className='analyze-btn-container'>
              <WFIDButton
                variant='filled'
                color='#151515'
                disabled={!file || !disclaimerAccepted || !privacyPolicyAccepted}
                width={130}
                onClick={handleUploadButtonClick}
                content='Evaluate'
              />
            </div>
            <div className='reset-fields-btn'>
              <WFIDButton width={130} bgColor='#FAFAFA' onClick={handleReset} content='Reset Fields' />
            </div>
          </div>
        </div>
        {/*TODO: print report will only be visible for verification at the moment*/}
        <div className='print-report-container'>
          <div className='disclaimer-container'>
            <a rel='noreferrer' target='_blank' href={DISCLAIMER_URL}>
              Disclaimer
            </a>
          </div>
          {/* <a>Download Report</a> */}
        </div>
        {showPostResultInfo && <ShowResult content={postResultInfoMessage} />}
      </div>

      <div className='right-side'>
        <div className='right-side-inner-container'>
          <div className='right-side-header'>
            <p>
              This map illustrates the geographic range of reference data used in the evaluation. It provides an
              overview of the available reference data 'area of insight' within the full genus distribution, to help you
              understand the conclusions of your evaluation.
            </p>
            <p>
              Confidence level bands (95%, 90%, and 85%) highlight broader areas where the model predicts the tree or
              plant is likely to have grown.{' '}
            </p>
          </div>
          <div id='applicationDiv1' className='' ref={mapViewRef} />
        </div>
      </div>
    </div>
  );
};

export default Determination;
